// The page's half of the native photo upload (BL-2683, ADR 070 — Ingest Protocol § 7.6): hand a lot's queued
// photos to the native transfer queue, then read back which rows finished. The foreground sender
// (`lot-media-sender.ts`) stays the executor for everything native does not take.
import { stagedPosterIdOf } from '@auctogon/admin-isomorphic/isomorphic/offline/offline-attachments';
import { ingestPlacement, markAddsNative, mediaOf, withoutAdd, type MediaAdd } from '@auctogon/admin-isomorphic/isomorphic/offline/lot-state-media';
import { planMediaHandoff } from '@auctogon/admin-isomorphic/isomorphic/offline/native-media-handoff';
import type { LotDesiredState } from '@auctogon/admin-isomorphic/isomorphic/offline/lot-desired-state';
import type { NativeTransferItem, NativeTransferQueue, OfflineMediaStaging, OfflineStore } from '../../gateways/native-device';
import { toMediaUploadItem } from '../../gateways/native-device/native-transfer/lanes';

/** What the hand-off needs from the replay that owns it. */
export interface LotMediaNativeDeps {
  store: OfflineStore;
  media?: OfflineMediaStaging | null;
  native: NativeTransferQueue;
  notify: () => void;
  logError?: (message: string, err: Error) => void;
}

const asError = (err: unknown): Error => (err instanceof Error ? err : new Error(String(err)));

/** The text a row carries, captured now (ADR 066 — Offline Mode § 8). */
function rowLabel(state: LotDesiredState): string {
  const { lotNumber, title } = state.label;
  return lotNumber != null && lotNumber !== '' ? `Photo for lot ${lotNumber}, ${title}` : `Photo for ${title}`;
}

/** The queue item for one photo, or null when its staged file is gone or its id cannot ride the lane. */
async function itemFor(deps: LotMediaNativeDeps, input: { state: LotDesiredState; add: MediaAdd }): Promise<NativeTransferItem | null> {
  const { add, state } = input;
  if (add.stagedFileId == null) return null;
  const staged = await deps.media?.get(add.stagedFileId);
  if (!staged) return null;
  const { position, isCover } = ingestPlacement(mediaOf(state), add);
  try {
    return toMediaUploadItem({
      label: rowLabel(state),
      payload: {
        kind: 'photo',
        lotId: state.lotId,
        mediaId: add.id,
        photoStagedId: add.stagedFileId,
        thumbStagedId: stagedPosterIdOf(add.stagedFileId),
        contentType: staged.mimeType,
        position,
        isCover,
      },
    });
  } catch (err) {
    // An id the lane cannot carry (not a v4 UUID): the foreground sends the photo.
    deps.logError?.('change queue: a photo cannot ride the native lane', asError(err));
    return null;
  }
}

/** Enqueue one item. @returns true when native holds the row afterwards (new, or already there). */
async function enqueueOne(deps: LotMediaNativeDeps, item: NativeTransferItem): Promise<boolean> {
  try {
    const result = await deps.native.enqueue({ items: [item] });
    if (result.success || result.code === 'DUPLICATE_ID') return true;
    // An unlisted lane or no active scope is the expected "native cannot take it": the foreground sends it.
    if (result.code === 'INVALID_ITEM') deps.logError?.('change queue: native refused a photo', new Error(result.error));
    return false;
  } catch (err) {
    deps.logError?.('change queue: the native queue could not take a photo', asError(err));
    return false;
  }
}

/**
 * Hand a lot's eligible queued photos to the native queue, one row each (row id = media id, so repeating it is safe).
 * A photo native does not take stays untouched for the foreground executor.
 *
 * @param deps - the store, staging and native queue.
 * @param input.lotId - the lot, which must already exist on the server.
 * @returns once every eligible photo has been tried.
 * @sideEffect Enqueues native rows; marks the handed-off adds `native` in the lot's state.
 */
export async function handOffMedia(deps: LotMediaNativeDeps, input: { lotId: string }): Promise<void> {
  const state = await deps.store.lotStates.get(input.lotId);
  if (!state || !deps.media) return;
  let lanes: string[];
  try {
    lanes = (await deps.native.capabilities()).lanes;
  } catch (err) {
    deps.logError?.('change queue: the native queue could not be read', asError(err));
    return;
  }
  const media = mediaOf(state);
  const thumbnailsStaged = new Set<string>();
  for (const add of media.adds) {
    if (add.stagedFileId != null && (await deps.media.get(stagedPosterIdOf(add.stagedFileId))) != null) thumbnailsStaged.add(add.stagedFileId);
  }
  const { handoff } = planMediaHandoff({ media, lanes, thumbnailsStaged });
  const handed: string[] = [];
  for (const add of handoff) {
    const item = await itemFor(deps, { state, add });
    if (item != null && (await enqueueOne(deps, item))) handed.push(add.id);
  }
  if (handed.length === 0) return;
  await deps.store.lotStates.update(input.lotId, (cur) => cur && { ...cur, media: markAddsNative(mediaOf(cur), handed) });
  deps.notify();
}

/**
 * Settle the lot's handed-off photos against the native rows. A photo whose staged files native no longer names is
 * finished: gone from the state, files untouched (native released them). One the operator discarded natively also leaves
 * the lot's order. A photo native still holds, failed ones included, keeps the lot waiting: a failed row is retried or
 * discarded from the Storage & Sync page, which is native's own.
 *
 * @param deps - the store and native queue.
 * @param input.lotId - the lot.
 * @returns whether any handed-off photo is still unfinished.
 * @sideEffect Removes finished adds from the lot's state.
 */
export async function reconcileNativeMedia(deps: LotMediaNativeDeps, input: { lotId: string }): Promise<{ waiting: boolean }> {
  const state = await deps.store.lotStates.get(input.lotId);
  const handed = state ? mediaOf(state).adds.filter((a) => a.native) : [];
  if (handed.length === 0) return { waiting: false };
  let unfinished: Set<string>;
  let discarded = new Set<string>();
  try {
    unfinished = new Set(await deps.native.stagedFileIds());
    if (handed.some((a) => !unfinished.has(a.stagedFileId ?? ''))) {
      discarded = new Set((await deps.native.listRows({ states: ['discarded'], limit: 500 })).map((r) => r.id));
    }
  } catch (err) {
    deps.logError?.('change queue: the native queue could not be read', asError(err));
    return { waiting: true };
  }
  const finished = handed.filter((a) => !unfinished.has(a.stagedFileId ?? ''));
  if (finished.length > 0) {
    await deps.store.lotStates.update(input.lotId, (cur) => {
      if (!cur) return cur;
      let media = mediaOf(cur);
      for (const add of finished) media = withoutAdd(media, add.id);
      // A recorded order must not name a photo the server never received.
      const dropped = finished.filter((a) => discarded.has(a.id)).map((a) => a.id);
      const order = media.order?.filter((id) => !dropped.includes(id)) ?? null;
      return { ...cur, media: { ...media, order: order != null && order.length > 0 ? order : null } };
    });
    deps.notify();
  }
  return { waiting: handed.length > finished.length };
}
